import { marked } from 'marked';
import TurndownService from 'turndown';

// Initialization
const lexer = new marked.Lexer();
const renderer = new marked.Renderer();
const lexerDefault = new marked.Lexer({
  tokenizer: new marked.Tokenizer(),
});
const linkRule = lexerDefault.tokenizer.rules.inline.link; //store default 'link' rule in order to configure at runtime
const originalRendererText = renderer.text.bind(renderer); // WEBMESS-931
const turndownService = new TurndownService();

let prevCharacter = '';
let isLinkIncluded = false; // Boolean to track if link is present within text
let bAllowInbound = true; //Boolean to track if we allow highlight extension to be used on inbound messages
let sMessageType = ''; //set default messageType as empty string

//Custom extensions for: monospace, Bold, Italic, Strikethrough, highlight and Code Block
const monospace = {
  name: 'monospace',
  level: 'inline', // Is this a block-level or inline-level tokenizer?
  start(src) {
    return src.match(/`/)?.index;
  }, // Hint to Marked.js to stop and check for a match
  tokenizer(src) {
    // regex that matches ` once followed by any character other than ` or any whitespace characters until the closing `
    const regexMonospace = /^(`)([^`]|[^`][\s\S]*?[^`])\1(?!`)/;
    const match = regexMonospace.exec(src); //check for match in src string using monospace regex

    if (match) {
      let text = match[2]; //get match[2] which is anything from within ` and `

      //remove whitespace around match before converting to html (similar to marked.js)
      const hasNonSpaceChars = /[^ ]/.test(text);
      const hasSpaceCharsOnBothEnds = /^ /.test(text) && / $/.test(text);

      if (hasNonSpaceChars && hasSpaceCharsOnBothEnds) {
        text = text.substring(1, text.length - 1);
      }

      return {
        type: 'monospace',
        raw: match[0],
        inner: this.lexer.inlineTokens(text),
      };
    }
  },
  renderer(token) {
    return `<code>${this.parser.parseInline(token.inner)}</code>`;
  },
};

const boldText = {
  name: 'boldText',
  level: 'inline',
  start(src) {
    return src.match(/\*(?!\s)/)?.index;// Hint to Marked.js to stop and check for a match
  },
  tokenizer(src) {
    if (!src.startsWith('*') || src.match(/^\*{2,}/)) {// write our own custom code to check if the pattern matches, extract the inner text and provide it to marked.js api so it can render with in the marked content.
      return;
    }

    //regex to check for first * not at the begininning of the string or preceeded by \
    const regexBold = /(?!^)(?!\\\*).\*/;
    const nextIndex = src.match(regexBold)?.index;

    if (nextIndex && nextIndex !== -1) {
      return {
        type: 'boldText',
        raw: src.slice(0, nextIndex + 2),
        inner: this.lexer.inlineTokens(src.slice(1, nextIndex + 1)),
      };
    }
  },
  renderer(token) {
    return `<strong>${this.parser.parseInline(token.inner)}</strong>`;
  },
};

const italicText = {
  name: 'italicText',
  level: 'inline',
  start(src) {
    //check for underscore and return index and add index -1 as prevchar.
    //In tokenizer check if prevchar is letter or number then return out else slice at index of match _

    // allow underscores within emails e.g testing_customer_care@email.com
    // regex will check for _ not immediately followed by whitespace (?!\s) or email address syntax
    const match = src.match(/_(?!\s)(?![A-Za-z0-9._+-]+(@)[a-zA-Z0-9-_]+(?:\.[a-zA-Z0-9-_]*[a-zA-Z0-9])+(?![-_]))/)?.index;

    if (prevCharacter && prevCharacter !== '') {
      prevCharacter = ''; // reset prevCharacter
    }

    if (match) {
      if (match !== 0) {
        prevCharacter = src.charAt(match - 1); //set prevCharacter
      }

      return match;
    }
  },
  tokenizer(src) {
    if (src.match(/^_{2,}/) || !src.match(/^_/) || !src.match(/^_[^\n\r\s].*[^\n\r\s]_(?![\p{L}\p{N}])/u)) {// check src starts with one underscore and not two. check src does not contain a newline/space after first underscore and before second underscore.
      return;
    }

    //check previous character is not a letter or number from any language
    if (prevCharacter && prevCharacter.match(/[\p{L}\p{N}]/u)) {
      return;
    }

    //regex to check for first _ not at the begininning of the string. Checks underscore is not preceeded by \ and no letters or numbers are immediately after
    const regexItalic = /(?!^)(?!\\_)._(?![\p{L}\p{N}])/u;
    const nextIndex = src.match(regexItalic)?.index;

    if (nextIndex && nextIndex !== -1) {
      return {
        type: 'italicText',
        raw: src.slice(0, nextIndex + 2),
        inner: this.lexer.inlineTokens(src.slice(1, nextIndex + 1)),
      };
    }
  },
  renderer(token) {
    return `<em>${this.parser.parseInline(token.inner)}</em>`;
  },
};

const strikethrough = {
  name: 'strikethrough',
  level: 'inline',
  start(src) {
    return src.match(/~(?!\s)/)?.index;// Hint to Marked.js to stop and check for a match
  },
  tokenizer(src) {
    if (!src.startsWith('~') || src.match(/^~{2,}/)) {// write our own custom code to check if the pattern matches, extract the inner text and provide it to marked.js api so it can render with in the marked content.
      return;
    }

    //regex to check for first ~ not at the begininning of the string or preceeded by \
    const regexStrike = /(?!^)(?!\\~).~/;
    const nextIndex = src.match(regexStrike)?.index;


    if (nextIndex && nextIndex !== -1) {
      return {
        type: 'strikethrough',
        raw: src.slice(0, nextIndex + 2),
        inner: this.lexer.inlineTokens(src.slice(1, nextIndex + 1)),
      };
    }
  },
  renderer(token) {
    return `<s>${this.parser.parseInline(token.inner)}</s>`;
  },
};

const codeBlock = {
  name: 'codeBlock',
  level: 'inline', // Is this a block-level or inline-level tokenizer?
  start(src) {
    return src.match(/```/)?.index;
  }, // Hint to Marked.js to stop and check for a match
  tokenizer(src) {
    // regex that matches ` three times followed by any character other than ` or any whitespace characters until the closing `
    const regexCode = /^(`{3})([^`]|[^`][\s\S]*?[^`])\1(?!`)/;
    const match = regexCode.exec(src); //check for match in src string using code block regex

    if (match) {
      let text = match[2];//get match[2] which is anything from within ``` and ```

      //remove whitespace around match before converting to html (similar to marked.js)
      const hasNonSpaceChars = /[^ ]/.test(text);
      const hasSpaceCharsOnBothEnds = /^ /.test(text) && / $/.test(text);

      if (hasNonSpaceChars && hasSpaceCharsOnBothEnds) {
        text = text.substring(1, text.length - 1);
      }

      return {
        type: 'codeBlock',
        raw: match[0],
        inner: this.lexer.inlineTokens(text),
      };
    }
  },
  renderer(token) {
    return `<pre><code>${this.parser.parseInline(token.inner)}</code></pre>`;
  },
};

const highLight = {
  name: 'highLight',
  level: 'inline', // Is this a block-level or inline-level tokenizer?
  start(src) {
    return src.match(/==/)?.index;
  }, // Hint to Marked.js to stop and check for a match
  tokenizer(src) {
    if (!bAllowInbound && sMessageType && sMessageType === 'inbound') {// check if inbound messages are allowed and if not then check for message of type inbound
      return;
    }
    // regex that matches == two times followed by any character other than = or any whitespace characters until the closing =
    const regexHighlight = /^(={2})([^=]|[^=][\s\S]*?[^=])\1(?!=)/;
    const match = regexHighlight.exec(src); //check for match in src string using highlight regex

    if (match) {
      const text = match[2];//get match[2] which is anything from within == and ==

      return {
        type: 'highLight',
        raw: match[0],
        inner: this.lexer.inlineTokens(text),
      };
    }
  },
  renderer(token) {
    return `<mark>${this.parser.parseInline(token.inner)}</mark>`;
  },
};

//function to configure markdown rules and only allow our custom extensions
export function configureMarkdown(options) {
  const { disableLinkFocus, enabled, highlight } = options || {};
  const { allowInbound } = highlight || {};
  const aInlineBlackList = [
    'heading',
    'autolink',
    'blockSkip',
    'br',
    'code',
    'del',
    'nolink',
    'overlapSkip',
    'punctuation',
    'reflink',
    'reflinkSearch',
    'tag',
  ];

  const aBlockBlackList = [
    'blockquote',
    'bullet',
    'code',
    'def',
    'fences',
    'heading',
    'hr',
    'html',
    'item',
    'lheading',
    'list',
    'newline',
    'nptable',
    'paragraph',
    'table',
    'text',
  ];

  //Allow links to open in new tab
  renderer.link = function () {
    const link = marked.Renderer.prototype.link.apply(this, arguments);

    isLinkIncluded = true; //set to true when link is present in text

    return disableLinkFocus
      ? link.replace('<a', '<a target=\'_blank\' tabindex=\'-1\'')
      : link.replace('<a', '<a target=\'_blank\''); //add tabindex '-1' only when the image preview is opened
  };

  // WEBMESS-931: over-ride the image rendered function to render it as a normal text message. Since we know what the syntax is, we will just render it as-is.
  renderer.image = function (href, title, text) {
    if (title)
      return originalRendererText(`![${text}](${href} "${title}")`);
    else
      return originalRendererText(`![${text}](${href})`);
  };

  marked.setOptions({
    renderer: renderer,
  });

  if (typeof allowInbound === 'boolean') bAllowInbound = allowInbound; //update top level boolean if the value is provided

  if (enabled) {
    marked.use({ extensions: [boldText, italicText, strikethrough, highLight, codeBlock, monospace] });
    //reset link rule to default
    lexer.tokenizer.rules.inline.link = linkRule;
  } else {
    //need to add 'link' back into the BlackList if false
    lexer.tokenizer.rules.inline.link = { exec: function () {} };
    marked.use({ extensions: false });
  }

  // WEBMESS-773: Override the markdown inline _backpedal rule to allow underscores at the end of URls e.g https://www.test.jp/abc_
  lexer.tokenizer.rules.inline._backpedal = /(?:[^?!.,:;*~()&]+|\([^)]*\)|&(?![a-zA-Z0-9]+;$)|[?!.,:;*_~)]+(?!$))+/;

  // Disable markdown, allow only links/url and emails.
  lexer.tokenizer.rules.inline.emStrong.lDelim = { exec: function () {} };
  lexer.tokenizer.rules.inline.emStrong.rDelimAst = { exec: function () {} };
  lexer.tokenizer.rules.inline.emStrong.rDelimUnd = { exec: function () {} };

  aInlineBlackList.map((key) => {
    lexer.tokenizer.rules.inline[key] = { exec: function () {} };
  });

  aBlockBlackList.map((key) => {
    lexer.tokenizer.rules.block[key] = { exec: function () {} };
  });
}

//function to return html using marked
export function parseMarkdown(options) {
  const { text } = options || '';

  if (text && typeof text === 'string' && text !== '') {
    const markedText = marked.parseInline(text);

    return markedText;
  } else {
    return;
  }
}

//function to return an object containing markedText and a Boolean isLinkIncluded
export function getMarkdown(options) {
  const { text, messageType } = options || '';

  if (text && typeof text === 'string' && text !== '') {
    //when messageType is provided we will update sMessageType
    if (messageType && typeof messageType === 'string' && messageType !== '') sMessageType = messageType.toLowerCase();

    const markedText = marked.parseInline(text);
    const oMarkdownText = { markedText: markedText, isLinkIncluded: isLinkIncluded };

    isLinkIncluded = false; //reset
    sMessageType = ''; //reset

    return oMarkdownText;
  } else {
    return;
  }
}

/**
 * To extend writing custom marked syntaxes using markdown extensions.
 */
export function extendMarkdown(options) {
  const { extensions } = options || [];

  if (extensions && extensions.length) {
    extensions.map((key) => {
      marked.use({ extensions: [key] });
    });
  }
}


/**
 * Function to configure html2markdown
 */
export function configureHtml2Markdown(rules) {
  if (rules && rules.length) {
    rules.map(({ key, value }) => {
      turndownService.addRule(key, value);
    });
  }
}

/**
 * Convert html to markdown using the default rules
 */
export function html2markdown(html) {
  if (html && typeof html === 'string' && html !== '') {
    return turndownService.turndown(html);
  }
}

/**
 * Let Turndown lib escape markdown inside HTML
 * https://github.com/mixmark-io/turndown?tab=readme-ov-file#overriding-turndownserviceprototypeescape
 */
export function turndownEscape() {
  TurndownService.prototype.escape = function (string) {
    return string;
  }
}

export default {
  configureMarkdown,
  parseMarkdown,
  getMarkdown,
  extendMarkdown,
  configureHtml2Markdown,
  html2markdown,
  turndownEscape,
};

